import React, { useEffect, useState } from 'react';
import { Table, Row, Col, Form, Icon } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import Search from 'antd/es/input/Search';
import { useStoreState, useStoreActions } from '../store/hooks';
import { MaxDiscountListItem, RuleStates } from '../api/types';
import UpdateButton from './UpdateButton';
import RuleStateCell from './RuleStateCell';
import SupplierInput from './SupplierInput';
import CategoryInput from './CategoryInput';
import ParameterInput from './ParameterInput';

export interface Props {
  onView: (id: number, type: 'PRODUCT' | 'PARAMETER') => void;
  onEdit: (id: number, type: 'PRODUCT' | 'PARAMETER') => void;
  onRemove: (id: number, type: 'PRODUCT' | 'PARAMETER') => void;
}

const MaxDiscountsTable: React.FC<Props> = ({ onEdit, onView, onRemove }) => {
  const { list: data, isLoading } = useStoreState(state => state.maxDiscounts);
  const { fetchList } = useStoreActions(actions => actions.maxDiscounts);
  const { formatMessage } = useIntl();

  const [categoryId, setCategoryId] = useState<string>('');
  const [supplierId, setSupplierId] = useState<string>('');
  const [productId, setProductId] = useState<string>('');
  const [parameterId, setParameterId] = useState<string>('');
  const [ean, setEan] = useState<string>('');

  const formItemLayout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 18 },
  };

  const fetchData = () => {
    fetchList({
      productId: productId === '' ? undefined : productId,
      ean: ean === '' ? undefined : ean,
      supplierId: supplierId === '' ? undefined : supplierId,
      categoryId: categoryId === '' ? undefined : categoryId,
      parameterId: parameterId === '' ? undefined : parameterId,
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ean, productId, supplierId, categoryId, parameterId]);

  return (
    <>
      <Row gutter={[16, 40]}>
        <Col>
          <Form className='pup-form'>
            <Row gutter={16}>
              <Col lg={7}>
                <Form.Item
                  label={
                    <FormattedMessage id='dashboard_products.table.titles.id' />
                  }
                  {...formItemLayout}
                >
                  <Search allowClear onSearch={value => setProductId(value)} />
                </Form.Item>
              </Col>
              <Col lg={7}>
                <Form.Item
                  label={
                    <FormattedMessage id='dashboard_products.table.titles.ean' />
                  }
                  {...formItemLayout}
                >
                  <Search allowClear onSearch={value => setEan(value)} />
                </Form.Item>
              </Col>

              <Col lg={7}>
                <SupplierInput
                  value={supplierId}
                  isFilter
                  onChange={setSupplierId}
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col lg={7}>
                <CategoryInput
                  value={categoryId}
                  isFilter
                  onChange={setCategoryId}
                />
              </Col>

              <Col lg={7}>
                <ParameterInput
                  value={parameterId}
                  isFilter
                  onChange={setParameterId}
                />
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>

      <Table<MaxDiscountListItem>
        loading={isLoading}
        dataSource={data.valueSeq().toArray()}
        size='middle'
        childrenColumnName='sourceMaxDiscounts'
        rowKey={rec => `${rec.id}_${rec.type}`}
        rowClassName={() => `c-pointer`}
        onRow={({ id, type }) => ({
          onClick: e => {
            e.preventDefault();
            onView(id, type);
          },
        })}
        columns={[
          {
            dataIndex: 'id',
            title: formatMessage({ id: 'max_discounts.table.titles.id' }),
            fixed: 'left',
            width: 70,
            align: 'center',
          },
          {
            dataIndex: 'type',
            title: formatMessage({
              id: 'max_discounts.table.titles.type',
            }),
            width: 120,
          },
          {
            dataIndex: 'category',
            title: formatMessage({
              id: 'max_discounts.table.titles.category',
            }),
            render: (text, { category }) => (
              <>{category ? category.name : ''}</>
            ),
          },
          {
            dataIndex: 'supplier',
            title: formatMessage({
              id: 'max_discounts.table.titles.supplier',
            }),
            render: (text, { supplier }) => (
              <>{supplier ? supplier.name : ''}</>
            ),
          },
          {
            dataIndex: 'parameter',
            title: formatMessage({
              id: 'max_discounts.table.titles.parameter',
            }),
            render: (text, { parameter }) => (
              <>{parameter ? parameter.name : ''}</>
            ),
          },
          {
            dataIndex: 'productId',
            title: formatMessage({
              id: 'max_discounts.table.titles.productId',
            }),
            width: 120,
          },
          {
            dataIndex: 'ean',
            title: formatMessage({
              id: 'max_discounts.table.titles.ean',
            }),
            width: 150,
          },
          {
            dataIndex: 'maxDiscount',
            title: formatMessage({
              id: 'max_discounts.table.titles.max_discount',
            }),
            width: 100,
            render: (text, { maxDiscount }) => <>{maxDiscount || 0}%</>,
          },
          {
            dataIndex: 'validFrom',
            title: formatMessage({
              id: 'max_discounts.table.titles.valid_from',
            }),
            width: 100,
          },
          {
            dataIndex: 'validTo',
            title: formatMessage({
              id: 'max_discounts.table.titles.valid_to',
            }),
            width: 100,
          },
          {
            dataIndex: 'state',
            title: formatMessage({
              id: 'max_discounts.table.titles.state',
            }),
            render: (text, { state }) => <RuleStateCell ruleState={state} />,
            width: 90,
          },
          {
            key: 'update-button',
            align: 'left',
            width: 125,
            className: 'action-column',
            render: (text, { id, type, state }) =>
              state !== RuleStates.LOCKED ? (
                <>
                  <UpdateButton
                    onClick={e => {
                      e.preventDefault();
                      onEdit(id, type);
                    }}
                  />
                  {(state === RuleStates.DRAFT ||
                    state === RuleStates.TO_DELETE) && (
                    <Icon
                      className='delete-icon'
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        onRemove(id, type);
                      }}
                      type='delete'
                    />
                  )}
                </>
              ) : (
                <></>
              ),
          },
        ]}
      />
    </>
  );
};

export default MaxDiscountsTable;
